// Business-day maths (spec 02 §5 add_business_days; the SQL twin arrives in batch 2 with these vectors).
import { zonedTimeToUtc } from "./torontoTime";

export const DEFAULT_HOLIDAYS: readonly string[] = Object.freeze([
  "2026-12-25",
  "2027-01-01",
]);
const DAY_MS = 86_400_000;

const toUtcDay = (ymd: string): Date => {
  const [y, m, d] = ymd.split("-").map(Number);
  const day = new Date(Date.UTC(y, m - 1, d));
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(ymd) ||
    day.getUTCFullYear() !== y ||
    day.getUTCMonth() !== m - 1 ||
    day.getUTCDate() !== d
  )
    throw new RangeError(`invalid calendar date: ${ymd}`);
  return day;
};
const ymdOf = (day: Date): string => day.toISOString().slice(0, 10);

export const DEFAULT_BUSINESS_DAYS: readonly number[] = Object.freeze([
  1, 2, 3, 4, 5,
]);

/** `businessDays` are ISO weekdays (1 = Monday … 7 = Sunday). */
export function isBusinessDay(
  ymd: string,
  holidays: readonly string[] = DEFAULT_HOLIDAYS,
  businessDays: readonly number[] = DEFAULT_BUSINESS_DAYS,
): boolean {
  const isoWeekday = ((toUtcDay(ymd).getUTCDay() + 6) % 7) + 1;
  return businessDays.includes(isoWeekday) && !holidays.includes(ymd);
}

/**
 * `n` business days after a Toronto calendar day, at `atTime` Toronto time (n = 0 keeps the day).
 * The start day itself may be a weekend or holiday: counting begins with the next day, and n = 0
 * returns `atTime` on that same (possibly non-business) day.
 */
export function addBusinessDays(
  startLocalDate: string,
  n: number,
  atTime: string,
  holidays: readonly string[] = DEFAULT_HOLIDAYS,
  businessDays: readonly number[] = DEFAULT_BUSINESS_DAYS,
): Date {
  if (!Number.isInteger(n) || n < 0)
    throw new RangeError("n must be a non-negative integer");
  if (
    n > 0 &&
    !businessDays.some((d) => Number.isInteger(d) && d >= 1 && d <= 7)
  )
    throw new RangeError("businessDays must contain an ISO weekday (1-7)");
  let day = toUtcDay(startLocalDate);
  let added = 0;
  while (added < n) {
    day = new Date(day.getTime() + DAY_MS);
    if (isBusinessDay(ymdOf(day), holidays, businessDays)) added += 1;
  }
  return zonedTimeToUtc(ymdOf(day), atTime);
}
